import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import BrandedButton from '@/components/buttons/BrandedButton';
import FileInputField from '@/components/inputs/FileInputField';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import { merchantDocumentsAcceptedFileTypes } from '@/constants/options/MerchantDocumentsAccetedFileTypes';
import { DocumentsData, documentsResolver } from '@/lib/validations/NpoPaymentOnboarding';
import { i18n } from '@/utils/i18n';

interface DocumentsFormProps {
  initialData?: DocumentsData;
  onDone?: (data: DocumentsData) => void;
  doneLabel?: string;
  isLoading?: boolean;
}

const DocumentsForm = ({
  initialData,
  onDone,
  doneLabel = i18n.t('mix.done'),
  isLoading = false
}: DocumentsFormProps) => {
  const MAX_FILE_SIZE = 4;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid, errors, isDirty }
  } = useForm<DocumentsData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: documentsResolver as any
  });

  const onSubmit: SubmitHandler<DocumentsData> = async (data) => {
    if (onDone) {
      onDone(data);
    }
  };

  useEffect(() => {
    if (initialData && !isDirty) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <View style={styles.formContainer}>
      <View style={styles.inputsContainer}>
        <OnboardingSection label={i18n.t('npoPaymentOnboarding.documents_form.voidedcheck_label')} gap={10}>
          <FileInputField
            control={control}
            name="voidedCheck"
            showError
            acceptedTypes={merchantDocumentsAcceptedFileTypes}
            getBlob
            maxFileSize={MAX_FILE_SIZE}
          />
        </OnboardingSection>
        <OnboardingSection label={i18n.t('npoPaymentOnboarding.documents_form.driverLicense_label')} gap={10}>
          <FileInputField
            control={control}
            name="driverLicense"
            showError
            acceptedTypes={merchantDocumentsAcceptedFileTypes}
            getBlob
            maxFileSize={MAX_FILE_SIZE}
          />
        </OnboardingSection>
        <OnboardingSection label={i18n.t('npoPaymentOnboarding.documents_form.doc501c3_label')} gap={10}>
          <FileInputField
            control={control}
            name="doc501c3"
            showError
            acceptedTypes={merchantDocumentsAcceptedFileTypes}
            getBlob
            maxFileSize={MAX_FILE_SIZE}
          />
        </OnboardingSection>
        <OnboardingSection label={i18n.t('npoPaymentOnboarding.documents_form.articlesOfIncorporation_label')} gap={10}>
          <FileInputField
            control={control}
            name="articlesOfIncorporation"
            acceptedTypes={merchantDocumentsAcceptedFileTypes}
            getBlob
            maxFileSize={MAX_FILE_SIZE}
          />
        </OnboardingSection>
        <OnboardingSection label={i18n.t('npoPaymentOnboarding.documents_form.bankStatements_label')} gap={10}>
          <FileInputField
            control={control}
            name="bankStatements"
            acceptedTypes={merchantDocumentsAcceptedFileTypes}
            getBlob
            maxFileSize={MAX_FILE_SIZE}
          />
          <Text style={styles.helperText}>{i18n.t('npoPaymentOnboarding.documents_form.bankStatements_helper')}</Text>
        </OnboardingSection>
        <Text style={styles.helperText}>
          {i18n.t('npoPaymentOnboarding.documents_form.helper_text', { MAX_FILE_SIZE: MAX_FILE_SIZE })}
        </Text>
      </View>
      {errors.root !== null && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

      <BrandedButton loading={isLoading} onPress={handleSubmit(onSubmit)} disabled={!isValid}>
        {doneLabel}
      </BrandedButton>
    </View>
  );
};

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    justifyContent: 'space-between'
  },

  inputsContainer: {
    gap: 30
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  },

  helperText: {
    color: 'gray',
    fontSize: 12,
    fontStyle: 'italic'
  }
});

export default DocumentsForm;
